<template lang="pug">
.resident-transfer-modal
  b-modal(v-model="_value" size="lg" @ok="ok" @cancel="cancel" no-close-on-backdrop no-close-on-esc)
    div(slot="modal-title")
      span 利用者・家族ユーザー情報を他施設へ移動もしくはコピー
    
    .d-flex.flex-column(style="position: relative;")
      .d-flex.flex-column.mb-2
        span.font-strong-color 移動／コピー先施設は事前設定が必要です。ご希望の場合はお問い合わせよりご連絡ください。
        span 利用者情報および家族ユーザー情報を他施設へ移動／コピーします。
        span 移動の場合は移動先／コピー先施設を選択後、「本施設の利用者・家族ユーザー情報を削除する」をチェックしてください。
        span チェックしない場合はコピーとなり、本施設にも利用者・家族ユーザー情報が残ります。

      .register-body.d-flex.flex-column.mb-2
        span(style="font-weight: bold;") 施設を選択してください
        .mb-2
        .resident-input
          FormInputSelect(v-model="targetFacilityId" name="facilityId" key-name="移動／コピー先施設" :options="facilityOptions"
            :validation-rules="{ required: true }" :styles="{ 'max-width': '540px' }" @change="changeTargetFacilityId" required)

      .register-body.d-flex.flex-column.mb-2(v-if="targetFacilityId")
        span(style="font-weight: bold;") 移動／コピー先施設の利用者情報を入力してください。
        .mb-2
        .resident-input
          FormInputSelect(v-model="roomId" name="room-id" key-name="部屋" :options="roomOptions" :validation-rules="{ required: true }"
            :styles="{ 'max-width': '540px' }" required)
          FormInputSelect(v-model="groupId" name="group-id" key-name="利用者グループ" :options="residentGroupOptions" :validation-rules="{ required: true }"
            :styles="{ 'max-width': '540px' }" required)
          FormDatetimePicker(v-model="inDatetime" :date.sync="inDatetimeDate" :time.sync="inDatetimeTime" name="in-date" key-name="入所日／入所予定日"
            placeholder="入所日／入所予定日")
          FormTimePicker(v-if="inDatetime" v-model="pickUpDatetime" :time.sync="pickUpDatetimeTime" name="pick-up-time" key-name="入所送迎時間" 
            placeholder="入所送迎時間")
          FormDatetimePicker(v-model="outDatetime" :date.sync="outDatetimeDate" :time.sync="outDatetimeTime" name="out-date" key-name="退所日／退所予定日"
            placeholder="退所日／退所予定日")
          FormTimePicker(v-if="outDatetime" v-model="dropOffDatetime" :time.sync="dropOffDatetimeTime" name="drop-off-time" key-name="退所送迎時間"
            placeholder="退所送迎時間")

      .d-flex.flex-column(v-if="targetFacilityId")
        span(style="font-weight: bold;") 本施設の利用者・家族ユーザー情報の削除有無
        .mb-2
        b-form-checkbox(v-model="needDelete" :value="true" :unchecked-value="false") 本施設の利用者・家族ユーザー情報を削除する

      .error-messages.mt-2(v-if="errorMessage")
          .error-message {{ errorMessage }}

    RelativeLoading(:isBusy="isBusy" :message="loadingMessage")
      
    template(slot="modal-footer" slot-scope="{ ok, cancel }" style="width:100%;")
      ValidationAlert(v-model="_showValidationAlert")
      .buttons.d-flex.justify-content-end
        button.btn.modal-ok-btn(type="button" @click="ok()") 送信
        button.btn.modal-cancel-btn(type="button" @click="cancel()") キャンセル
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";
import { userStore } from "@/store/store";
import CustomValidate from "@/commons/utils/CustomValidate";

import FacilitiesService, { FacilityGetModel } from "@/commons/services/Facilities";
import { ResidentTransferPostModel } from "@/commons/services/Residents";
import RoomsService, { RoomGetModel, RoomType, UseType } from "@/commons/services/Rooms";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import RelativeLoading from "@/pc/basics/RelativeLoading.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormInput from "@/pc/components/forms/FormInput.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormDatetimePicker from "@/pc/components/forms/FormDatetimePicker.vue";
import FormTimePicker from "@/pc/components/forms/FormTimePicker.vue";

/**
 * ### 利用者の移動／コピーモーダル
 */
@Component({
  components: {
    RelativeLoading,
    ValidationAlert,
    FormInput,
    FormInputSelect,
    FormDatetimePicker,
    FormTimePicker
  },
  $_veeValidate: { validator: "new" }
})
export default class TransferResidentInputModal extends Vue {
  /** 施設サービス */
  @Inject("FacilitiesService") facilitiesService!: FacilitiesService;
  /** 部屋サービス */
  @Inject("RoomsService") roomsService!: RoomsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 表示可否 */
  @Prop({ required: true })
  value!: boolean;

  /** 利用者移動／コピーによるエラーメッセージ */
  @Prop()
  errorMessage!: string;

  /** ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 移動／コピー先の施設 */
  targetFacilityId: number | null = null;
  /** 移動／コピー先の施設選択肢 */
  facilityOptions: Array<{ value: number; label: string }> = [];
  /** 部屋ID */
  roomId: number | null = null;
  /** 部屋セレクトボックス用項目 */
  roomOptions: Array<{ value: number | string; label: string }> = [];
  /** グループ */
  groupId: string | null = null;
  /** グループセレクトボックス用項目 */
  residentGroupOptions: Array<{ value: number | string; label: string }> = [];
  /** 保険者番号 */
  insuredNumber: string = "";
  /** 介護保険被保険者番号 */
  careInsuranceInsuredNumber: string = "";
  /** 入所日 */
  inDatetime: number | null = null;
  /** 入所日(日) */
  inDatetimeDate: string = "";
  /** 入所日(時) */
  inDatetimeTime: string = "";
  /** 入所送迎時間 */
  pickUpDatetime: string | null = null;
  /** 入所送迎時間(時) */
  pickUpDatetimeTime: string = "";
  /** 退所日 */
  outDatetime: number | null = null;
  /** 退所日(日) */
  outDatetimeDate: string = "";
  /** 退所日(時) */
  outDatetimeTime: string = "";
  /** 退所送迎時間 */
  dropOffDatetime: string | null = null;
  /** 退所送迎時間(時) */
  dropOffDatetimeTime: string = "";
  /** 移動後、現施設から削除するか否か */
  needDelete: boolean = false;

  get _value(): boolean {
    return this.value;
  }

  set _value(newValue: boolean) {
    this.$emit("input", newValue);
    return;
  }

  get _showValidationAlert(): boolean {
    return this.showValidationAlert;
  }

  set _showValidationAlert(newValue: boolean) {
    this.showValidationAlert = false;
    return;
  }

  /**
   * ### vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   *
   * - 部屋の選択肢を作成する
   * - 利用者グループの選択肢を作成する
   * - 入所日の初期値を作成する
   * - 入所送迎時間の初期値を作成する
   * - 退所予定日の初期値を作成する
   * - 退所送迎時間の初期値を作成する
   * - 家族連絡サービスを使用している場合のみ、変更後ステップに「利用者」を追加する
   */
  public async createView(): Promise<void> {
    this.loadingMessage = "読み込み中...";
    this.isBusy = true;

    this.targetFacilityId = null;
    this.needDelete = false;
    const targetFacilities: Array<FacilityGetModel> = await this.getTargetFacilities();
    const facilityOptions: Array<{ value: number; label: string }> = this.createFacilityOptions(targetFacilities);
    this.facilityOptions = facilityOptions;

    this.isBusy = false;

    return;
  }

  public initialize(): void {
    this.roomId = null;
    this.groupId = null;

    const inDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const inDatetimeTime: string = "10:00:00";
    this.inDatetimeDate = inDatetimeDate;
    this.inDatetimeTime = inDatetimeTime;
    this.pickUpDatetimeTime = "10:00:00";

    const outDatetimeDate: string = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    const outDatetimeTime: string = "10:00:00";
    this.outDatetimeDate = outDatetimeDate;
    this.outDatetimeTime = outDatetimeTime;
    this.dropOffDatetimeTime = "10:00:00";

    return;
  }

  /**
   * ### 移動先対象施設取得
   */
  public async getTargetFacilities(): Promise<Array<FacilityGetModel>> {
    const targetFacilities: Array<FacilityGetModel> = [];
    for (const targetFacilityId of userStore.facility.residentTransferableFacilities) {
      const response: FacilityGetModel = await this.facilitiesService.get(userStore, { targetFacilityId: targetFacilityId });
      targetFacilities.push(response);
    }
    return targetFacilities;
  }

  public createFacilityOptions(targetFacilities: Array<FacilityGetModel>): Array<{ value: number; label: string }> {
    let facilityOptions: Array<{ value: number; label: string }> = [{ value: null as any, label: "" }];
    for (const targetFacility of targetFacilities) {
      facilityOptions.push({
        value: targetFacility.id,
        label: targetFacility.name
      });
    }
    return facilityOptions;
  }

  /**
   * ### 移動先施設の変更イベント
   *
   * - 移動先施設の部屋、利用者グループを取得して選択肢にする
   */
  public async changeTargetFacilityId(): Promise<void> {
    if (!this.targetFacilityId) {
      return;
    }

    this.loadingMessage = "読込み中...";
    this.isBusy = true;

    const rooms: Array<RoomGetModel> = await this.getRooms(this.targetFacilityId);
    const roomOptions: Array<{ value: number; label: string }> = this.createRoomOptions(rooms);
    this.roomOptions = roomOptions;

    const residentGroups: Array<ResidentGroupsGetModel> = await this.getResidentGroups(this.targetFacilityId);
    const residentGroupOptions: Array<{ value: number; label: string }> = this.createResidentGroupOptions(residentGroups);
    this.residentGroupOptions = residentGroupOptions;

    this.initialize();
    this.isBusy = false;

    return;
  }

  /**
   * ### 部屋取得
   */
  public async getRooms(facilityId: number): Promise<Array<RoomGetModel>> {
    const response: Array<RoomGetModel> = await this.roomsService.gets(userStore, { targetFacilityId: facilityId });
    return response;
  }

  public createRoomOptions(rooms: Array<RoomGetModel>): Array<{ value: number; label: string }> {
    const roomOptions: Array<{ value: number; label: string }> = [];
    for (const room of rooms) {
      roomOptions.push({
        value: room.id,
        label: `${room.name}（${this.getUseTypeName(room.useType)}・${this.getRoomTypeName(room.roomType)}）`
      });
    }
    return roomOptions;
  }

  public getUseTypeName(useType: number): string {
    if (useType == UseType.UNMANAGED) {
      return "空床管理対象外";
    } else if (useType == UseType.NORMAL_STAY) {
      return "入居用部屋";
    } else if (useType == UseType.SHORT_STAY) {
      return "ショートステイ";
    } else {
      return "";
    }
  }

  public getRoomTypeName(roomType: number): string {
    if (roomType == RoomType.UNMANAGED) {
      return "空床管理対象外";
    } else if (roomType == RoomType.SINGLE) {
      return "個室";
    } else if (roomType == RoomType.DOUBLE) {
      return "２人部屋（夫婦部屋）";
    } else if (roomType == RoomType.MULTI_MEN) {
      return "多床室（男性）";
    } else if (roomType == RoomType.MULTI_WOMEN) {
      return "多床室（女性）";
    } else {
      return "";
    }
  }

  /**
   * ### 利用者グループ取得
   */
  public async getResidentGroups(facilityId: number): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore, { targetFacilityId: facilityId });
    return response;
  }

  /**
   * ### グループセレクトボックス用項目を作成する
   */
  public createResidentGroupOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: number; label: string }> {
    const residentGroupOptions: Array<{ value: number; label: string }> = [];
    for (const group of residentGroups) {
      residentGroupOptions.push({
        value: group.id,
        label: group.name
      });
    }
    return residentGroupOptions;
  }

  public async ok(bvModalEvent: any): Promise<void> {
    bvModalEvent.preventDefault();

    const validationResult: boolean = await this.$validator.validateAll();
    const inDateTimeValidationResult: boolean = await this.validateInDateTime(); // validateAll()より後に実行しないと動かないので注意
    if (validationResult && inDateTimeValidationResult) {
      this.showValidationAlert = false;
      this.$emit("ok", this.createResidentTransferPostModel());
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /** 入所日時・退所日時のチェックは自前で行う */
  public async validateInDateTime(): Promise<boolean> {
    this.$validator.errors.remove("in-date");
    if (this.inDatetime && this.outDatetime) {
      if (this.outDatetime < this.inDatetime) {
        this.$validator.errors.add({ field: "in-date", msg: "入所日が退所日より未来です" });
        return false;
      } else {
        return true;
      }
    }
    return true;
  }

  public createResidentTransferPostModel(): ResidentTransferPostModel {
    // 入所送迎時間、退所送迎時間は入所日と退所送迎日の日付を使用する
    let pickUpDatetime: number | null = null;
    if (this.inDatetime && this.pickUpDatetime) {
      const pickUpDate: string = this.$dateUtil.convertNumberToString(this.inDatetime, "yyyymmdd");
      const pickUpTime: string = this.$dateUtil.replaceNonNumeric(this.pickUpDatetime); // time format is hh:MM:ss
      pickUpDatetime = Number(`${pickUpDate}${pickUpTime}000`);
    }
    let dropOffDatetime: number | null = null;
    if (this.outDatetime && this.dropOffDatetime) {
      const dropOffDate: string = this.$dateUtil.convertNumberToString(this.outDatetime, "yyyymmdd");
      const dropOffTime: string = this.$dateUtil.replaceNonNumeric(this.dropOffDatetime); // time format is hh:MM:ss
      dropOffDatetime = Number(`${dropOffDate}${dropOffTime}000`);
    }

    // 入所・退所情報が1つでもあればそれを配列に格納し、なければ空配列を格納する
    let inAndOutDatetimes = [];
    if (this.inDatetime || this.outDatetime || pickUpDatetime || dropOffDatetime) {
      inAndOutDatetimes.push({
        inDatetime: this.inDatetime ? this.inDatetime : null,
        pickUpDatetime: pickUpDatetime ? pickUpDatetime : null,
        outDatetime: this.outDatetime ? this.outDatetime : null,
        dropOffDatetime: dropOffDatetime ? dropOffDatetime : null,
        roomId: this.roomId as number
      });
    }

    return {
      destinationFacilityId: this.targetFacilityId as number,
      groupId: Number(this.groupId),
      roomId: this.roomId as number,
      inAndOutDatetimes: inAndOutDatetimes,
      needDelete: this.needDelete
    };
  }

  public cancel(): void {
    this.showValidationAlert = false;
    this.targetFacilityId = null;
    this.needDelete = false;
    this.$emit("cancel");
    return;
  }
}
</script>
